
import React, { useState } from 'react';
import { format } from 'date-fns';
import { CalendarEvent } from '@/types/calendar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { EditEventDialog } from './EditEventDialog';
import { DeleteEventDialog } from './DeleteEventDialog';
import { getEventColor, getEventIcon } from '@/utils/eventUtils';
import { 
  Edit, 
  Trash2, 
  Copy, 
  Share, 
  Clock, 
  MapPin, 
  Users, 
  Bot,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import AIAssistant from '@/components/ai/AIAssistant';

interface EventDetailDialogProps {
  event: CalendarEvent | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const EventDetailDialog: React.FC<EventDetailDialogProps> = ({
  event,
  isOpen,
  onClose,
  onEdit,
  onDelete
}) => {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);

  if (!event) return null;

  const EventIcon = getEventIcon(event.type);
  const isPastEvent = new Date(event.end_time) < new Date();

  const handleDuplicate = () => {
    // TODO: Implement duplicate functionality
    toast.info("Duplicate functionality coming soon");
  };

  const handleShare = () => {
    // TODO: Implement share functionality
    toast.info("Share functionality coming soon");
  };

  const handleEditClick = () => {
    setShowEditDialog(true);
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EventIcon className="h-5 w-5" />
              {event.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Event Type Badge */}
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className={getEventColor(event.type)}>
                {event.type || 'Other'}
              </Badge>
              {event.source === 'ai' && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <Bot className="h-3 w-3" />
                  AI Created
                </Badge>
              )}
            </div>

            {/* Time and Duration */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">
                  {format(new Date(event.start_time), 'EEEE, MMMM d, yyyy')}
                </span>
              </div>
              <div className="text-sm text-muted-foreground ml-6">
                {format(new Date(event.start_time), 'h:mm a')} - {format(new Date(event.end_time), 'h:mm a')}
              </div>
            </div>

            {/* Location */}
            {event.location && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>{event.location}</span>
              </div>
            )}

            {/* Attendees */}
            {event.attendees && event.attendees.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Attendees ({event.attendees.length})</span>
                </div>
                <div className="ml-6 space-y-1">
                  {event.attendees.map((attendee, index) => (
                    <div key={index} className="text-sm text-muted-foreground">
                      {attendee}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            {event.description && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Description</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {event.description}
                </p>
              </div>
            )}

            <Separator />

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="default" 
                size="sm" 
                onClick={handleEditClick}
                disabled={isPastEvent}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleDeleteClick}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDuplicate}
              >
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleShare}
              >
                <Share className="h-4 w-4 mr-2" />
                Share
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowAIAssistant(true)}
                disabled={isPastEvent}
              >
                <Bot className="h-4 w-4 mr-2" />
                Prepare with AI
              </Button>
            </div>

            {/* Event Metadata */}
            <div className="text-xs text-muted-foreground space-y-1 pt-4 border-t">
              <div>Created: {format(new Date(event.created_at || event.start_time), 'MMM d, yyyy h:mm a')}</div>
              {event.updated_at && event.updated_at !== event.created_at && (
                <div>Updated: {format(new Date(event.updated_at), 'MMM d, yyyy h:mm a')}</div>
              )}
              <div>Event ID: {event.id}</div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <EditEventDialog
        event={event}
        isOpen={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        onSuccess={() => {
          setShowEditDialog(false);
          onEdit();
        }}
      />

      {/* Delete Dialog */}
      <DeleteEventDialog
        event={event}
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onSuccess={() => {
          setShowDeleteDialog(false);
          onDelete();
          onClose();
        }}
      />

      {/* AI Assistant Dialog */}
      <Dialog open={showAIAssistant} onOpenChange={setShowAIAssistant}>
        <DialogContent className="max-w-4xl h-[80vh] p-0 flex flex-col">
          <DialogHeader className="p-4 border-b shrink-0">
            <DialogTitle>BrigAI Assistant - Preparing for: {event.title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden h-full">
            <AIAssistant event={event} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
